import { defineStore } from 'pinia';
import { useAuthStore } from 'stores/auth';
import { api } from 'boot/axios';
import { Patient } from 'src/types/member';
import { SaveOptions } from 'stores/medication';
import { saveSyncItem } from 'src/logic/offline';
import { useMemberStore } from 'stores/member';

export const useFormStore = defineStore('form', {
  persist: true,
  state: (): FormState => ({
    formTemplates: [] as FormTemplate[],
    formInstances: [] as FormInstance[],
    pendingFormInstances: [] as FormInstance[],
  }),
  getters: {
    getFormTemplate:
      (state: FormState) =>
      (GUID: string): FormTemplate | undefined => {
        return state.formTemplates.find((formTemplate) => formTemplate.GUID === GUID);
      },
    getFormTemplateByID:
      (state: FormState) =>
      (ID: number): FormTemplate | undefined => {
        return state.formTemplates.find((formTemplate) => formTemplate.ID === ID);
      },
    getFormInstance:
      (state: FormState) =>
      (GUID: string): FormInstance | undefined => {
        const instance: FormInstance | undefined = state.formInstances.find(
          (formInstance) => formInstance.GUID === GUID,
        );
        if (!instance) {
          return state.pendingFormInstances.find((formInstance) => formInstance.GUID === GUID);
        }
      },
    getIsPending:
      (state: FormState) =>
      (GUID: string): boolean => {
        const instance = state.pendingFormInstances.find(
          (formInstance) => formInstance.GUID === GUID,
        );
        return !!instance;
      },
    getNonCovidFormTemplates: (state: FormState) => (): FormTemplate[] => {
      return state.formTemplates.filter((formTemplate) => {
        return formTemplate.ID > 2;
      });
    },
    getCaseForms:
      (state: FormState) =>
      (patientRecordID: number, includePending: boolean): FormInstance[] => {
        const forms: FormInstance[] = state.formInstances.filter((formInstance) => {
          return formInstance.patientRecordID === patientRecordID;
        });
        if (includePending) {
          return forms.concat(
            state.pendingFormInstances.filter((formInstance) => {
              return formInstance.patientRecordID === patientRecordID;
            }),
          );
        } else {
          return forms;
        }
      },
    getPatientCovidForms:
      (state: FormState) =>
      (patientID: number): FormInstance[] => {
        return state.formInstances.filter(
          (f) =>
            // If the Patient ID matches, and it's form ID 1 or 2
            f.patientID === patientID && (f.templateID === 1 || f.templateID === 2),
        );
      },
  },
  actions: {
    async dataRefresh() {
      await this.fetchFormTemplates();
    },
    // Save form template
    saveFormTemplateLocally(formTemplate: FormTemplate) {
      // Check if form template already exists
      const index = this.formTemplates.findIndex((ft) => ft.GUID === formTemplate.GUID);
      if (index === -1) {
        // Add new form template
        this.formTemplates.push(formTemplate);
      } else {
        // Add the new form template to the list
        this.formTemplates.splice(index, 1, formTemplate);
      }
    },
    // Save many form templates
    saveFormTemplatesLocally(formTemplates: FormTemplate[]): void {
      if (formTemplates.length > 0) {
        formTemplates.forEach((formTemplate) => {
          this.saveFormTemplateLocally(formTemplate);
        });
      }
    },
    // Save form instance
    saveFormInstanceLocally(formInstance: FormInstance) {
      // Check if form instance already exists
      const index = this.formInstances.findIndex((fi) => fi.GUID === formInstance.GUID);
      if (index === -1) {
        // Add new form instance
        this.formInstances.push(formInstance);
      } else {
        // Add the new form instance to the list
        this.formInstances[index] = formInstance;
      }
    },
    // Save many form instances
    saveFormInstancesLocally(formInstances: FormInstance[]) {
      formInstances.forEach((formInstance) => {
        this.saveFormInstanceLocally(formInstance);
      });
    },
    // Save a pending instance
    saveLocallyPendingFormInstance(formInstance: FormInstance) {
      //Check if form instance already exists
      const index = this.pendingFormInstances.findIndex((fi) => fi.GUID === formInstance.GUID);
      if (index === -1) {
        // Add new form instance
        this.pendingFormInstances.push(formInstance);
      } else {
        // Add the new form instance to the list
        this.pendingFormInstances[index] = formInstance;
      }
    },
    async postPendingFormInstance(formInstance: FormInstance): Promise<FormInstance | void> {
      try {
        // Pre-emptively remove the local version
        this.deletePendingFormInstance(formInstance);
        // Save the form instance to the server
        return await this.postFormInstance(formInstance);
        // Remove the form instance from the pending list
      } catch (error) {
        // If it errored out. Resave the local instance
        this.saveLocallyPendingFormInstance(formInstance);
        console.error('Error saving pending form instance', error);
      }
    },
    deletePendingFormInstance(formInstance: FormInstance) {
      this.pendingFormInstances = this.pendingFormInstances.filter(
        (fi) => fi.GUID !== formInstance.GUID,
      );
    },
    async fetchFormTemplates(clientID?: number) {
      // Check if clientID is provided
      if (!clientID) {
        // Get clientID from auth store
        const authStore = useAuthStore();
        clientID = Number(authStore.clientId);
        if (Number.isNaN(clientID)) {
          throw new Error('Unable to find valid clientID');
        }
      }
      try {
        const res = await api.get(`/api/v2/clients/${clientID}/standardforms`);
        const data: FormTemplate[] = res.data;
        this.saveFormTemplatesLocally(data);
      } catch (e) {
        console.error(e);
      }
    },
    async fetchFormTemplate(GUID: string, clientID?: number) {
      if (!clientID) {
        const authStore = useAuthStore();
        clientID = Number(authStore.clientId);
        if (Number.isNaN(clientID)) {
          throw new Error('Unable to find valid clientID');
        }
      }
      try {
        const res = await api.get(`/api/v2/clients/${clientID}/standardforms/${GUID}`);
        const data: FormTemplate = res.data;
        this.saveFormTemplateLocally(data);
      } catch (e) {
        console.error(e);
      }
    },
    // Get Form Instances for patient
    async fetchFormInstances(patientRecordID: number, clientID?: number) {
      if (!clientID) {
        const authStore = useAuthStore();
        clientID = Number(authStore.clientId);
        if (Number.isNaN(clientID)) {
          throw new Error('Unable to find valid clientID');
        }
      }
      try {
        const res = await api.get(
          `/api/v2/clients/${clientID}/cases/${patientRecordID}/standardforms/instances`,
        );
        const data: FormInstance[] = res.data;
        this.saveFormInstancesLocally(data);
      } catch (e) {
        console.error(e);
      }
    },
    async fetchFormInstance(GUID: string, clientID?: number) {
      if (!clientID) {
        const authStore = useAuthStore();
        clientID = Number(authStore.clientId);
        if (Number.isNaN(clientID)) {
          throw new Error('Unable to find valid clientID');
        }
      }
      try {
        const res = await api.get(`/api/v2/clients/${clientID}/standardforms/${GUID}`);
        const data: FormInstance = res.data;
        this.saveFormInstanceLocally(data);
      } catch (e) {
        console.error(e);
      }
    },
    async postFormInstance(instance: FormInstance): Promise<FormInstance> {
      const instanceClone: FormInstance = {
        GUID: instance.GUID,
        ID: instance.ID,
        templateGUID: instance.templateGUID,
        templateID: instance.templateID,
        name: instance.name,
        status: instance.status,
        nextFlaggedTemplateID: instance.nextFlaggedTemplateID,
        nextFlaggedTemplateGUID: instance.nextFlaggedTemplateGUID,
        nextDefaultTemplateID: instance.nextDefaultTemplateID,
        nextDefaultTemplateGUID: instance.nextDefaultTemplateGUID,
        flaggedText: instance.flaggedText,
        flagQuery: instance.flagQuery,
        answerFlag: instance.answerFlag,
        date: instance.date,
        employeeID: instance.employeeID,
        patientRecordID: instance.patientRecordID,
        patientID: instance.patientID,
        lastUpdate: instance.lastUpdate,
        editedBy: instance.editedBy,
        fields: instance.fields,
      };
      if (instanceClone.patientRecordID === undefined) {
        const memberStore = useMemberStore();
        const patient: Patient = memberStore.getMemberByPatientId(
          instanceClone.patientID,
        ) as Patient;
        if (!patient?.clientID) {
          throw new Error('Unable to find patient');
        }
        const saveOptions: SaveOptions = {
          saveLocally: this.saveFormInstanceLocally,
          item: instanceClone,
          postPath: `/api/v2/clients/${patient.clientID}/standardforms/instances`,
        };
        return (await saveSyncItem(saveOptions)) as FormInstance;
      }
      const saveOptions: SaveOptions = {
        saveLocally: this.saveFormInstanceLocally,
        item: instanceClone,
        postPath: `/api/v2/cases/${instanceClone.patientRecordID}/standardforms/instances`,
      };
      return (await saveSyncItem(saveOptions)) as FormInstance;
    },
  },
});

export interface FormState {
  formTemplates: FormTemplate[];
  formInstances: FormInstance[];
  pendingFormInstances: FormInstance[];
}

export interface FormTemplate {
  GUID: string;
  ID: number;
  name: string;
  status: number;
  fields: FormTemplateField[];
  flagQuery: string;
  flagText: string;
  nextDefaultGUID: string;
  nextDefaultID: number;
  nextFlaggedGUID: string;
  nextFlaggedID: number;
  lastUpdate: number;
  editable: number;
}

export interface FormTemplateField {
  GUID: string;
  ID: number;
  number: string;
  type: number;
  prompt: string;
  required: number;
  lastUpdate: number;
  templateGUID: string;
  templateID: number;
  options: FormTemplateFieldOption[];
}

export interface FormTemplateFieldOption {
  GUID: string;
  ID: number;
  fieldGUID: string;
  templateGUID: string;
  value: string;
  label: string;
  lastUpdate: number;
}

export type FormInstance = {
  GUID: string;
  ID?: number;
  templateGUID: string;
  templateID: number;
  name: string;
  status: number;
  nextFlaggedTemplateID: number;
  nextFlaggedTemplateGUID: string;
  nextDefaultTemplateID: number;
  nextDefaultTemplateGUID: string;
  flaggedText: string;
  flagQuery: string;
  answerFlag: number;
  date: number;
  employeeID?: number;
  patientRecordID?: number;
  patientID?: number;
  lastUpdate: number;
  editedBy?: number;
  fields: FormInstanceField[];
  _messageType?: string;
};

export type FormInstanceField = {
  GUID: string;
  ID?: number;
  instanceGUID: string;
  instanceID?: number;
  templateGUID: string;
  templateID: number;
  number: string;
  prompt: string;
  type: number;
  required: number;
  lastUpdate: number;
  response: string | null;
  options: FormInstanceFieldOption[];
};

// TODO Add Order to FormInstanceFieldOption
export type FormInstanceFieldOption = {
  GUID: string;
  value: string;
  label: string;
  fieldGUID: string;
  order?: number;
  instanceGUID: string;
  lastUpdate: number;
};
